import { useState, useEffect, useCallback, useRef } from 'react';
import { normalizeRole } from '../../../utils/assignmentRules';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';

/**
 * Hook to manage projects data for RaiseQueryForm
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isActive - Whether form is active
 * @param {Object} params.currentUser - Current user object
 * @param {string} params.region - Selected region from form
 * @param {string} params.initialProjectName - Initial project name from URL
 * @param {Function} params.onProjectAutoSelected - Callback when project is auto-selected
 * @param {Object} params.formData - Form data object
 * @param {Function} params.setFormData - Setter for form data
 * @returns {Object} Projects state and handlers
 */
export const useRaiseQueryProjects = ({
  isActive,
  currentUser,
  region,
  initialProjectName,
  onProjectAutoSelected,
  formData,
  setFormData,
}) => {
  const { showSnackbar } = useSnackbar();
  
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const hasAutoFilledProjectRef = useRef(false);

  const fetchProjects = useCallback(async (userForFiltering, selectedRegion) => {
    setLoadingProjects(true);
    try {
      const currentUserData =
        userForFiltering ||
        currentUser ||
        (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user')) : null);

      if (!currentUserData) {
        showSnackbar('User information not found', 'error');
        setProjects([]);
        return;
      }

      const response = await api.get('/projects', {
        params: {
          limit: 1000, // Get all projects
        },
      });
      
      // Handle different possible response structures
      let allProjectsData = [];
      if (response.data && response.data.data) {
        // The API returns: response.data.data.projects
        allProjectsData = response.data.data.projects || [];
      } else if (response.data && Array.isArray(response.data)) {
        allProjectsData = response.data;
      } else if (response.data && response.data.projects) {
        allProjectsData = response.data.projects;
      }

      // Ensure allProjects is an array
      if (!Array.isArray(allProjectsData)) {
        // console.error('Projects data is not an array:', response.data);
        allProjectsData = [];
      }

      // Filter projects based on user role and region
      let filteredProjects = [];

      const userRole = normalizeRole(currentUserData.role);
      const userRegion = currentUserData.region;

      setAllProjects(allProjectsData);

      if (userRole === 'ADMIN') {
        filteredProjects = allProjectsData;
      } else if (userRole === 'CH' || userRole === 'MP') {
        const effectiveRegion = selectedRegion || '';
        filteredProjects = effectiveRegion
          ? allProjectsData.filter((project) => project.region === effectiveRegion)
          : [];
      } else if (userRole === 'ED' || userRole === 'GM') {
        filteredProjects = allProjectsData.filter(
          (project) => project.region === userRegion
        );
      } 
      // Default: show all projects for any other role
      else {
        filteredProjects = allProjectsData;
      }

      setProjects(filteredProjects);
    } catch (error) {
      showSnackbar('Failed to load projects', 'error');
      // console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [showSnackbar, currentUser]);

  // Filter projects based on selected region
  useEffect(() => {
    if (!currentUser) return;
    const role = normalizeRole(currentUser.role);
    
    // Filter projects based on selected region
    if (region) {
      const filtered = allProjects.filter((project) => project.region === region);
      setProjects(filtered);
    } else {
      // If no region selected, show empty projects list for CH/MP
      if (role === 'CH' || role === 'MP') {
        setProjects([]);
      } else {
        // For other roles, show projects based on their own region
        const userRegion = currentUser.region || '';
        const filtered = allProjects.filter((project) => project.region === userRegion);
        setProjects(filtered);
      }
    }
  }, [region, allProjects, currentUser]);

  // Auto-fill project name from URL params when projects are loaded
  useEffect(() => {
    if (!isActive || !initialProjectName || projects.length === 0 || hasAutoFilledProjectRef.current) {
      return;
    }

    // If user already picked a project (manual or restored), do not override it.
    if (formData.projectName) {
      hasAutoFilledProjectRef.current = true;
      return;
    }

    const needle = initialProjectName.trim().toLowerCase();
    const primary = (p) => (p.description || p.name || '').trim();
    const matchingProject = projects.find((p) => {
      if (!needle) return false;
      if (p.name && p.name.toLowerCase() === needle) return true;
      if (p.description && p.description.toLowerCase() === needle) return true;
      return false;
    });

    if (matchingProject) {
      const projectNameValue = matchingProject.description || matchingProject.name || '';
      setFormData((prev) => ({
        ...prev,
        projectName: projectNameValue,
        projectCode: matchingProject.code || '',
      }));
      hasAutoFilledProjectRef.current = true;
      if (onProjectAutoSelected) {
        onProjectAutoSelected(projectNameValue);
      }
    }
  }, [isActive, initialProjectName, projects, formData.projectName, onProjectAutoSelected, setFormData]);

  // Initial projects fetch
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const storedUser = currentUser || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user')) : null);
    const regionForFetch = region;
    fetchProjects(storedUser, regionForFetch);
    // Intentionally depend only on `isActive` to avoid refetch loops when state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const resetProjects = () => {
    setProjects([]);
    setAllProjects([]);
  };

  return {
    projects,
    allProjects,
    loadingProjects,
    fetchProjects,
    resetProjects,
  };
};
